import { HttpError } from "@/dashboard/lib/api/errors";

export interface FetchJsonOptions extends RequestInit {
  tenantId?: string | null;
}

let activeTenantId: string | null = null;

/** Set the tenant sent when a caller omits tenantId; the cookie fallback is shared across windows. */
export function setActiveTenantIdForFetch(tenantId: string | null) {
  activeTenantId = tenantId;
}

export type FetchJsonResponse<T> = {
  data: T;
  headers: Headers;
};

/** Like fetchJson, while retaining response headers needed by a small number of consistency APIs. */
export async function fetchJsonWithResponse<T>(
  url: string,
  init?: FetchJsonOptions
): Promise<FetchJsonResponse<T>> {
  const { tenantId, headers, ...restInit } = init || {};

  // Normalize headers to a plain object
  const normalizedHeaders: Record<string, string> = {
    "Content-Type": "application/json",
  };

  // Convert Headers object or array to plain object
  if (headers) {
    if (headers instanceof Headers) {
      headers.forEach((value, key) => {
        normalizedHeaders[key] = value;
      });
    } else if (Array.isArray(headers)) {
      headers.forEach(([key, value]) => {
        normalizedHeaders[key] = value;
      });
    } else {
      Object.assign(normalizedHeaders, headers);
    }
  }

  // Include tenant ID from React state in headers (source of truth)
  if (tenantId) {
    normalizedHeaders["x-tenant-id"] = tenantId;
  } else if (activeTenantId) {
    normalizedHeaders["x-tenant-id"] = activeTenantId;
  }

  const res = await fetch(url, {
    credentials: "include", // always include cookies
    headers: normalizedHeaders,
    ...restInit,
  });

  if (!res.ok) {
    let msg = `Request failed (${res.status})`;
    let code: string | undefined;
    try {
      const errorBody = (await res.json()) as { error?: unknown; code?: unknown };
      if (typeof errorBody.error === "string") {
        msg = errorBody.error;
      }
      if (typeof errorBody.code === "string") {
        code = errorBody.code;
      }
    } catch {
      // swallow
    }

    throw new HttpError(res.status, msg, code);
  }

  // 204 No Content → return undefined
  return { data: (res.status === 204 ? undefined : await res.json()) as T, headers: res.headers };
}

export async function fetchJson<T>(url: string, init?: FetchJsonOptions): Promise<T> {
  return (await fetchJsonWithResponse<T>(url, init)).data;
}
